import React, { useRef, useState } from "react";
import { CancelDialog, ExpandCard } from "@components";
import { NewModuleSetting, UpdatedModuleSetting, RootProductId } from "@models";
import { ModuleSettingCardHeader } from "./ModuleSettingCardHeader";
import ModuleSettingConfiguration from "./ModuleSettingConfiguration";
import { isNewModuleSetting } from "./util";
import { ExpandCardRef } from "components/ExpandCard";
import { getDateFlags } from "@util";
import styles from "@theme/styles";

type Props = {
	moduleSetting: UpdatedModuleSetting | NewModuleSetting;
	rootProductId: RootProductId;
	isInitiallyExpanded?: boolean;
	className?: string;
	discardModuleSetting?: () => void;
	onCopy?: () => void;
};

export default function ModuleSettingCard(props: Props) {
	const { moduleSetting, rootProductId, isInitiallyExpanded } = props;

	const [showCancelDialog, setShowCancelDialog] = useState(false);
	const [mustShowCloseWarning, setMustShowCloseWarning] = useState(false);
	const expandCardRef = useRef<ExpandCardRef>(null);

	const isNew = isNewModuleSetting(moduleSetting);

	const collapseCard = () => {
		expandCardRef?.current?.collapse();
		if (isNew && props.discardModuleSetting) {
			props.discardModuleSetting();
		}
	};

	const onCloseEditMode = (needsConfirmation: boolean) => {
		if (needsConfirmation && (mustShowCloseWarning || isNew)) {
			setShowCancelDialog(true);
			return false;
		} else {
			collapseCard();
			return true;
		}
	};

	const uniqueId =
		"moduleSettingId" in moduleSetting ? moduleSetting.moduleSettingId : "new";

	const { isActive } = getDateFlags(
		moduleSetting.availableFrom,
		moduleSetting.availableUntil
	);

	const header = (
		<ModuleSettingCardHeader
			moduleSetting={moduleSetting}
			rootProductId={rootProductId}
			onCopy={() => {
				if (props.onCopy) props.onCopy();
			}}
		/>
	);

	return (
		<ExpandCard
			className={props.className}
			testId={"moduleSettingCard-" + uniqueId}
			header={header}
			isInitiallyExpanded={isInitiallyExpanded}
			ref={expandCardRef}
			isActive={isActive}
			onCollapse={() => onCloseEditMode(true)}
			css={isNew ? styles.NewCard : undefined}
		>
			<ModuleSettingConfiguration
				key={"moduleSettingConfig-" + uniqueId}
				moduleSetting={moduleSetting}
				onClose={onCloseEditMode}
				onFormDirty={setMustShowCloseWarning}
			/>

			<CancelDialog
				showAlertDialog={showCancelDialog}
				cancelEditMode={collapseCard}
				hideDialog={() => setShowCancelDialog(false)}
			>
				Sie haben ungespeicherte Änderungen in der Produktkonfiguration.
				Änderungen wirklich verwerfen?
			</CancelDialog>
		</ExpandCard>
	);
}
